import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Spinner, Alert, Btn, Input, Select, Modal, SectionCard } from '../../components/UI';
import { api } from '../../utils/api';
import { ramp, cityLabel, empLabel } from './FindClients';
import UsageAlert from '../../components/UsageAlert';

// HB Sales Tool W4 — All clients · My clients, Import clients, the client page with
// "Latest facts (Sync)" and ✦ Sync (1 · Prompt, 2 · Answer, 3 · Approve). Visual contract v3.

const LIMIT = 400;
const TIERS = 'Lists per city: A — highest potential, B — high potential, C — other. Potential = the strongest fit + half of the second.';

const pill = { display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: '0.74rem', fontWeight: 700, marginRight: 4, marginBottom: 2, whiteSpace: 'nowrap' };
const tag = { display: 'inline-block', padding: '1px 8px', borderRadius: 999, fontSize: '0.72rem', fontWeight: 700, border: '1px solid var(--line)', color: 'var(--body-ink)', whiteSpace: 'nowrap' };
const th = { textAlign: 'left', padding: '8px 10px', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--muted)', borderBottom: '1px solid var(--line)', whiteSpace: 'nowrap' };
const td = { padding: '9px 10px', borderBottom: '1px solid var(--line)', verticalAlign: 'top', fontSize: '0.86rem' };
const muted = { color: 'var(--muted)', fontSize: '0.76rem' };
const host = (u) => String(u || '').replace(/^https?:\/\//, '').split('/')[0];
const mil = (rev) => (rev != null ? Math.round(rev / 1000).toLocaleString('en-GB') : '—');

export function FitPill({ name, v }) {
  return <span title={`${name}: ${v}`} style={{ ...pill, background: ramp(v / 100) }}>{name.split(' ').map(w => w[0]).join('').slice(0, 3)} {v}</span>;
}
const TierTag = ({ t }) => <span style={{ ...tag, ...(t === 'A' ? { background: 'var(--ink)', color: '#fff', borderColor: 'var(--ink)' } : {}) }}>{t}</span>;

// ── HB Clients: All clients · My clients ───────────────────────────────────
export function ClientsRegistry() {
  const [tab, setTab] = useState('all');
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [q, setQ] = useState('');
  const [city, setCity] = useState('');
  const [tier, setTier] = useState('');
  const [open, setOpen] = useState(null);       // company id on the client page
  const [sync, setSync] = useState(null);       // client for ✦ Sync
  const [importing, setImporting] = useState(false);

  const load = useCallback(() => {
    api.explore.findClients(0)
      .then(d => setState({ loading: false, error: null, data: d }))
      .catch(e => setState({ loading: false, error: e.message, data: null }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const d = state.data;
  const names = useMemo(() => Object.fromEntries((d?.programmes || []).map(p => [p.slug, p.name])), [d]);
  const cities = useMemo(() => {
    const n = {}; (d?.clients || []).forEach(c => { if (c.city) n[c.city] = (n[c.city] || 0) + 1; });
    return Object.entries(n).sort((a, b) => b[1] - a[1]).map(([k]) => k);
  }, [d]);
  const mine = useMemo(() => (d?.clients || []).filter(c => c.mine), [d]);
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (tab === 'my' ? mine : d?.clients || []).filter(c =>
      (!city || c.city === city) && (!tier || c.list === tier) && (!s || `${c.name} ${c.short || ''} ${c.mb || ''}`.toLowerCase().includes(s)));
  }, [d, mine, tab, q, city, tier]);
  const top = (c, n) => Object.entries(c.fit || {}).sort((a, b) => b[1] - a[1]).slice(0, n);

  async function removeMine(c) {
    try { await api.explore.removeMyClient(c.id); load(); } catch (e) { alert(e.message); }
  }

  if (state.loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><Spinner /></div>;
  if (state.error) return <Alert type="error">{state.error}</Alert>;

  return (
    <>
      <Card style={{ padding: '12px 18px', marginBottom: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn size="sm" variant={tab === 'all' ? 'primary' : 'outline'} onClick={() => setTab('all')}>All clients ({d.clients.length})</Btn>
            <Btn size="sm" variant={tab === 'my' ? 'primary' : 'outline'} onClick={() => setTab('my')}>My clients ({mine.length})</Btn>
          </div>
          <Btn size="sm" variant="outline" onClick={() => setImporting(true)}>Import clients</Btn>
        </div>
      </Card>

      <Card style={{ padding: '14px 18px', marginBottom: 14 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <Input type="search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search by name or MB…" style={{ flex: 1, minWidth: 220 }} />
          <Select value={city} onChange={e => setCity(e.target.value)} style={{ width: 'auto' }}>
            <option value="">City: all</option>
            {cities.map(c => <option key={c} value={c}>{cityLabel(c)}</option>)}
          </Select>
          <Select value={tier} onChange={e => setTier(e.target.value)} style={{ width: 'auto' }}>
            <option value="">List: all</option>
            {['A', 'B', 'C'].map(t => <option key={t} value={t}>List {t}</option>)}
          </Select>
          <span style={muted}>{list.length} firms</span>
        </div>
        <p style={{ ...muted, fontSize: '0.8rem', margin: '8px 0 0' }}>{TIERS}</p>
      </Card>

      <Card style={{ padding: '6px 10px', overflow: 'auto', marginBottom: 14 }}>
        {!list.length ? (
          <p style={{ ...muted, fontSize: '0.88rem', padding: 12, margin: 0 }}>
            {tab === 'my' ? 'No clients on your list yet. Import them, or open a client from All clients and click "+ My clients".' : 'No firm matches these filters.'}
          </p>
        ) : tab === 'all' ? (
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead><tr>{['#', 'Client', 'City', 'Sector', 'Employees', 'Revenue (RSD m)', 'List', 'Strongest fit'].map((h, i) =>
              <th key={h} style={{ ...th, textAlign: i === 4 || i === 5 ? 'right' : 'left' }}>{h}</th>)}</tr></thead>
            <tbody>
              {list.slice(0, LIMIT).map(c => (
                <tr key={c.id} onClick={() => setOpen(c.id)} style={{ cursor: 'pointer' }}>
                  <td style={{ ...td, ...muted }}>{c.cityRank}</td>
                  <td style={td}><b>{c.short || c.name}</b>{c.mine && <> <span style={tag}>My clients</span></>}</td>
                  <td style={td}>{cityLabel(c.city)}<div style={muted}>{c.municipality}</div></td>
                  <td style={td}>{c.sector || '—'}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{empLabel(c)}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{mil(c.revenue)}</td>
                  <td style={td}><TierTag t={c.list} /></td>
                  <td style={td}>{top(c, 3).map(([s, v]) => <FitPill key={s} name={names[s] || s} v={v} />)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead><tr>{['Client', 'City', 'Sector', 'Employees', 'List', 'Strongest fit', 'Facts', ''].map((h, i) =>
              <th key={i} style={{ ...th, textAlign: i === 3 ? 'right' : 'left' }}>{h}</th>)}</tr></thead>
            <tbody>
              {list.map(c => (
                <tr key={c.id}>
                  <td style={td}>
                    <button onClick={() => setOpen(c.id)} style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', fontWeight: 700, color: 'var(--ink)', textAlign: 'left' }}>{c.short || c.name}</button>
                    {!c.mb && <> <span style={tag}>imported</span></>}
                    <div style={muted}>{c.mb ? `MB ${c.mb}` : 'no MB'}</div>
                  </td>
                  <td style={td}>{cityLabel(c.city) || '—'}</td>
                  <td style={td}>{c.sector || '—'}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{empLabel(c)}</td>
                  <td style={td}><TierTag t={c.list} /></td>
                  <td style={td}>{top(c, 2).map(([s, v]) => <FitPill key={s} name={names[s] || s} v={v} />) }{!top(c, 1).length && '—'}</td>
                  <td style={td}>{c.facts ? c.facts : <span style={muted}>—</span>}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <Btn size="sm" onClick={() => setSync(c)}>✦ Sync</Btn>{' '}
                    <Btn size="sm" variant="outline" onClick={() => removeMine(c)}>Remove</Btn>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {tab === 'all' && list.length > LIMIT && <p style={{ ...muted, padding: '8px 10px' }}>Showing the first {LIMIT} of {list.length} — search or filter to narrow the list.</p>}
      </Card>

      {tab === 'my' && (
        <SectionCard title="Sync — the latest facts about a client">
          <ol style={{ fontSize: '0.88rem', margin: 0, paddingLeft: 18, lineHeight: 1.6, color: 'var(--body-ink)' }}>
            <li><b>Prompt</b> — Sync prepares a prompt for Claude or Microsoft 365 Copilot with what we know about the client.</li>
            <li><b>Answer</b> — paste the answer back: one fact per line, each with its source link.</li>
            <li><b>Approve</b> — accept fact by fact. A fact that supports an observed criterion marks it "yes" for the client, and the fit is recalculated.</li>
          </ol>
        </SectionCard>
      )}

      {open && <ClientPage companyId={open} names={names} fits={d.clients.find(c => c.id === open)}
        onClose={() => setOpen(null)} onChanged={load} onSync={(c) => setSync(c)} />}
      {sync && <SyncModal client={sync} onClose={() => setSync(null)} onChanged={load} />}
      {importing && <ImportModal onClose={() => setImporting(false)} onDone={() => { setImporting(false); setTab('my'); load(); }} />}
    </>
  );
}

// ── The client page: data, fit per programme, Latest facts (Sync) ──────────
function ClientPage({ companyId, names, fits, onClose, onChanged, onSync }) {
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { api.explore.getClientProfile(companyId).then(setP).catch(e => setError(e.message)); }, [companyId]);
  useEffect(() => { load(); }, [load]);

  async function addMine() {
    setBusy(true);
    try { await api.explore.addMyClient(companyId); load(); onChanged(); } catch (e) { setError(e.message); } finally { setBusy(false); }
  }
  async function openProject(slug) {
    try { const r = await api.explore.openFromFindClients({ companyId, programme: slug }); navigate(`/customers/${r.id}`); }
    catch (e) { setError(e.message); }
  }

  const c = p?.client;
  const fitRows = Object.entries(fits?.fit || {}).sort((a, b) => b[1] - a[1]);
  const accepted = (p?.facts || []).filter(f => f.status === 'accepted');
  const data = c ? [
    ['MB', c.mb || '—'], ['Registered name', c.name], ['Sector', c.sector || '—'],
    ['Activity', [c.nace, c.activity].filter(Boolean).join(' — ') || '—'],
    ['City', [cityLabel(c.city), c.municipality].filter(Boolean).join(', ') || '—'], ['Legal form', c.legalFormEn || '—'],
    ['Employees', c.employees != null ? c.employees.toLocaleString('en-GB') : '—'], ['Revenue (RSD m)', mil(c.revenue)],
    ['List', fits ? `${fits.list} · rank ${fits.cityRank} (${cityLabel(fits.city)})` : '—'],
  ] : [];

  return (
    <Modal open onClose={onClose} width={860} title={c ? (c.short || c.name) : 'Client'}>
      {error && <Alert type="error">{error}</Alert>}
      {!p ? <Spinner /> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {[...p.derived, ...p.observed.map(o => o.criterion)].map(k => <span key={k} style={tag}>{k}</span>)}
          </div>
          <div>
            <h4 style={secH}>Data</h4>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}><tbody>
              {data.map(([a, b]) => <tr key={a}><td style={{ ...td, ...muted, fontSize: '0.84rem', width: '30%' }}>{a}</td><td style={td}>{b}</td></tr>)}
            </tbody></table>
          </div>
          <div>
            <h4 style={secH}>Fit per programme</h4>
            {!fitRows.length ? <p style={muted}>No programme fits this client yet.</p> : (
              <table style={{ borderCollapse: 'collapse', width: '100%' }}><tbody>
                {fitRows.map(([s, v]) => {
                  const opened = (fits.projects || []).find(x => x.programme === s);
                  return (
                    <tr key={s}>
                      <td style={{ ...td, width: '34%' }}><b>{names[s] || s}</b>
                        <div style={{ height: 6, background: 'var(--field)', borderRadius: 3, marginTop: 4 }}><div style={{ width: `${v}%`, height: 6, borderRadius: 3, background: ramp(v / 100) }} /></div></td>
                      <td style={{ ...td, fontWeight: 700, width: '8%' }}>{v}</td>
                      <td style={{ ...td, ...muted, fontSize: '0.82rem' }}>why: {(fits.why?.[s] || []).join(', ') || '—'}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap', width: '1%' }}>
                        <Btn size="sm" variant="outline" onClick={() => (opened ? navigate(`/customers/${opened.id}`) : openProject(s))}>{opened ? 'Open the project' : 'Open a project'}</Btn>
                      </td>
                    </tr>
                  );
                })}
              </tbody></table>
            )}
          </div>
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <h4 style={{ ...secH, margin: 0 }}>Latest facts (Sync)</h4>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {p.mine ? <span style={tag}>on My clients</span> : <Btn size="sm" variant="outline" loading={busy} onClick={addMine}>+ My clients</Btn>}
                <Btn size="sm" onClick={() => onSync({ id: companyId, short: c.short, name: c.name })}>✦ Sync</Btn>
              </div>
            </div>
            <FactsTable facts={accepted} empty="No synced facts yet. Sync collects the latest public facts — leadership, growth and hiring, the sales and HR set-up, news — each with its source." />
          </div>
        </div>
      )}
    </Modal>
  );
}
const secH = { fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--muted)', margin: '0 0 8px' };

function FactsTable({ facts, empty, compact }) {
  if (!facts.length) return <p style={{ ...muted, fontSize: '0.84rem', margin: '8px 0 0', lineHeight: 1.5 }}>{empty}</p>;
  return (
    <table style={{ borderCollapse: 'collapse', width: '100%', marginTop: 8 }}>
      {!compact && <thead><tr>{['Field', 'Fact', 'Criterion', 'Checked', 'Source'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>}
      <tbody>
        {facts.map(f => (
          <tr key={f.id}>
            <td style={td}><b>{f.field}</b>{compact && f.sourceDate && <div style={muted}>{String(f.sourceDate).slice(0, 10)}</div>}</td>
            <td style={td}>{f.fact}{compact && f.criterion && <div><span style={tag}>{f.criterion}</span></div>}</td>
            {!compact && <td style={td}>{f.criterion ? <span style={tag}>{f.criterion}</span> : '—'}</td>}
            {!compact && <td style={td}>{f.sourceDate ? String(f.sourceDate).slice(0, 10) : '—'}</td>}
            <td style={td}>{f.sourceUrl ? <a href={f.sourceUrl} target="_blank" rel="noopener noreferrer">{host(f.sourceUrl)}</a> : '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── ✦ Sync: 1 · Prompt, 2 · Answer, 3 · Approve ────────────────────────────
export function SyncModal({ client, onClose, onChanged }) {
  const [prompt, setPrompt] = useState('');
  const [answer, setAnswer] = useState('');
  const [facts, setFacts] = useState(null);       // proposed facts to approve
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.explore.getSyncPrompt(client.id).then(r => setPrompt(r.prompt)).catch(e => setError(e.message));
    api.explore.getClientFacts(client.id).then(r => setFacts(r.facts.filter(f => f.status === 'proposed'))).catch(() => setFacts([]));
  }, [client.id]);

  const proposed = (list) => setFacts(list.filter(f => f.status === 'proposed'));
  async function run() {
    setBusy('run'); setError(null);
    try { proposed((await api.explore.runSync(client.id)).facts); } catch (e) { setError(e.message); } finally { setBusy(''); }
  }
  async function read() {
    setBusy('read'); setError(null);
    try { proposed((await api.explore.pasteSyncAnswer(client.id, answer)).facts); setAnswer(''); } catch (e) { setError(e.message); } finally { setBusy(''); }
  }
  async function decide(f, decision) {
    try {
      await api.explore.decideFact(f.id, decision);
      if (decision === 'accept') await api.explore.addMyClient(client.id).catch(() => {});
      setFacts(list => list.map(x => (x.id === f.id ? { ...x, status: decision === 'accept' ? 'accepted' : 'declined' } : x)));
      onChanged && onChanged();
    } catch (e) { setError(e.message); }
  }
  async function acceptAll() {
    setBusy('all');
    for (const f of facts.filter(x => x.status === 'proposed' && x.sourceUrl)) await decide(f, 'accept');
    setBusy('');
  }
  function copy() {
    (navigator.clipboard ? navigator.clipboard.writeText(prompt) : Promise.reject()).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }).catch(() => {});
  }

  return (
    <Modal open onClose={onClose} width={860} title={`✦ Sync — ${client.short || client.name}`}>
      <UsageAlert />
      {error && <Alert type="error">{error}</Alert>}
      <p style={{ fontSize: '0.88rem', margin: '0 0 6px' }}><b>1 · Prompt.</b> Copy it into Claude or Microsoft 365 Copilot (with web search on) — or let Basecamp search now.</p>
      <textarea readOnly value={prompt} style={{ ...area, height: 150, fontSize: '0.76rem' }} />
      <div style={{ display: 'flex', gap: 8, margin: '6px 0 14px', flexWrap: 'wrap' }}>
        <Btn size="sm" onClick={copy} disabled={!prompt}>{copied ? 'Copied' : 'Copy the prompt'}</Btn>
        <Btn size="sm" variant="outline" loading={busy === 'run'} onClick={run} disabled={!prompt}>Search now in Basecamp</Btn>
      </div>
      <p style={{ fontSize: '0.88rem', margin: '0 0 6px' }}><b>2 · Answer.</b> Paste the answer here.</p>
      <textarea value={answer} onChange={e => setAnswer(e.target.value)} style={{ ...area, minHeight: 110 }}
        placeholder="Leadership | New Commercial Director appointed | https://… | 03.10.2026 | new leadership / reorganisation" />
      <div style={{ marginTop: 6 }}><Btn size="sm" loading={busy === 'read'} disabled={!answer.trim()} onClick={read}>Read the answer</Btn></div>

      {facts && facts.length > 0 && (
        <>
          <p style={{ fontSize: '0.88rem', margin: '16px 0 6px' }}><b>3 · Approve.</b> Accept fact by fact. A fact without a source link cannot be accepted.</p>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead><tr>{['Field', 'Fact', 'Criterion', 'Source', ''].map((h, i) => <th key={i} style={th}>{h}</th>)}</tr></thead>
            <tbody>
              {facts.map(f => (
                <tr key={f.id}>
                  <td style={td}><b>{f.field}</b><div style={muted}>{f.sourceDate ? String(f.sourceDate).slice(0, 10) : ''}</div></td>
                  <td style={td}>{f.fact}</td>
                  <td style={td}>{f.criterion ? <span style={tag}>{f.criterion}</span> : '—'}</td>
                  <td style={td}>{f.sourceUrl ? <a href={f.sourceUrl} target="_blank" rel="noopener noreferrer">{host(f.sourceUrl)}</a> : <span style={{ color: 'var(--gate)' }}>no link</span>}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    {f.status === 'proposed' ? (
                      <>
                        {f.sourceUrl && <Btn size="sm" onClick={() => decide(f, 'accept')}>Accept</Btn>}{' '}
                        <Btn size="sm" variant="outline" onClick={() => decide(f, 'decline')}>Decline</Btn>
                      </>
                    ) : <span style={muted}>{f.status === 'accepted' ? '✓ accepted' : 'declined'}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: 8 }}>
            <Btn size="sm" variant="outline" loading={busy === 'all'} disabled={!facts.some(x => x.status === 'proposed' && x.sourceUrl)} onClick={acceptAll}>Accept all with a source</Btn>
          </div>
        </>
      )}
    </Modal>
  );
}
const area = { width: '100%', boxSizing: 'border-box', padding: 10, border: '1px solid var(--field-line)', borderRadius: 8, background: 'var(--field)', fontFamily: 'inherit', fontSize: '0.86rem' };

// ── Import clients ─────────────────────────────────────────────────────────
function ImportModal({ onClose, onDone }) {
  const [text, setText] = useState('');
  const [prev, setPrev] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  async function loadFile(e) {
    const f = e.target.files[0];
    if (!f) return;
    try {
      // CSV with a header row: MB; Name; City; Activity code; Employees (comma, semicolon or tab).
      const lines = (await f.text()).split(/\r?\n/).filter(l => l.trim());
      setText(lines.slice(/[a-z]/i.test(lines[0] || '') && !/^\d/.test(lines[0]) ? 1 : 0).map(l => l.replace(/,/g, ';')).join('\n'));
    } catch (err) { setError(`The file could not be read: ${err.message}`); }
  }
  async function preview() {
    setBusy(true); setError(null);
    try { setPrev(await api.explore.previewMyClientsImport(text)); } catch (e) { setError(e.message); } finally { setBusy(false); }
  }
  async function commit() {
    setBusy(true); setError(null);
    try { await api.explore.importMyClients(text); onDone(); } catch (e) { setError(e.message); setBusy(false); }
  }

  return (
    <Modal open onClose={onClose} width={760} title="Import clients">
      {error && <Alert type="error">{error}</Alert>}
      <p style={{ ...muted, fontSize: '0.86rem', lineHeight: 1.5, marginTop: 0 }}>
        One client per line: <b>MB</b> or <b>name</b>; optionally more columns separated by tab or semicolon — MB; Name; City; Activity code; Employees.
        Or load a CSV file with those columns. Firms on the shared list are matched by MB (or exact name); the rest are added as new clients.
      </p>
      <textarea value={text} onChange={e => { setText(e.target.value); setPrev(null); }} style={{ ...area, minHeight: 140 }}
        placeholder={'20457759\n12345678; Nordwind Logistics d.o.o.; Beograd; 4941; 420'} />
      <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center' }}>
        <label style={{ cursor: 'pointer' }}>
          <input type="file" accept=".csv,.txt" onChange={loadFile} style={{ display: 'none' }} />
          <span style={{ display: 'inline-block', padding: '6px 14px', border: '1px solid var(--line)', borderRadius: 999, fontSize: '0.84rem', fontWeight: 700 }}>Load file…</span>
        </label>
        <Btn size="sm" loading={busy && !prev} disabled={!text.trim()} onClick={preview}>Preview</Btn>
      </div>
      {prev && (
        <>
          <div style={{ maxHeight: 300, overflow: 'auto', marginTop: 12, border: '1px solid var(--line)', borderRadius: 8 }}>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead><tr>{['MB', 'Name', 'Match'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {prev.rows.map(r => (
                  <tr key={r.line}>
                    <td style={td}>{r.mb || '—'}</td>
                    <td style={td}>{r.matchedName || r.name || '—'}</td>
                    <td style={td}>{r.status === 'match' ? 'shared list' : r.status === 'new' ? <span style={tag}>new client</span> : <span style={{ color: 'var(--gate)' }}>not found — add the name</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: 10 }}>
            <Btn loading={busy} disabled={!(prev.counts.match + prev.counts.new)} onClick={commit}>Add {prev.counts.match + prev.counts.new} to My clients</Btn>
          </div>
        </>
      )}
    </Modal>
  );
}

// ── Latest facts — side card in the customer workspace ─────────────────────
export function LatestFacts({ companyId, name }) {
  const [facts, setFacts] = useState(null);
  const [sync, setSync] = useState(false);
  const load = useCallback(() => {
    if (!companyId) return;
    api.explore.getClientFacts(companyId).then(r => setFacts(r.facts.filter(f => f.status === 'accepted'))).catch(() => setFacts([]));
  }, [companyId]);
  useEffect(() => { load(); }, [load]);
  if (!companyId) return null;
  return (
    <SectionCard title="Latest facts" action={<Btn size="sm" onClick={() => setSync(true)}>✦ Sync</Btn>}>
      {facts === null ? <Spinner /> : <FactsTable compact facts={facts.slice(0, 6)} empty="No synced facts yet. Sync collects the latest public facts, each with its source." />}
      {sync && <SyncModal client={{ id: companyId, name }} onClose={() => { setSync(false); load(); }} onChanged={load} />}
    </SectionCard>
  );
}
